import "../css/header.scss";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";

function Header() {
  const { cartCount, wishlist } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const isCheckout = location.pathname === "/checkout";
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setMoreOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
        setMoreOpen(false);
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);

  const results = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return productData.slice(0, 4);
    return productData
      .filter((product) =>
        [product.name, product.category, product.department, ...product.tags]
          .join(" ")
          .toLowerCase()
          .includes(query)
      )
      .slice(0, 5);
  }, [searchTerm]);

  const submitSearch = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : "/shop");
    toTop();
  };

  if (isCheckout) {
    return (
      <header className="checkout-header">
        <Link className="checkout-header__brand" to="/" onClick={toTop}>
          <img src="/brand-lockup.svg" alt="yorthmarketco" />
        </Link>
        <Link className="checkout-header__back" to="/cartpage" onClick={toTop}>
          Return to bag ({cartCount})
        </Link>
      </header>
    );
  }

  return (
    <>
      <header className="site-header">
        <div className="announcement">
          Tracked delivery {formatPrice(siteConfig.shipping.standard)}
        </div>
        <div className="site-header__bar">
          <button
            className="site-header__menu-button"
            type="button"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
          </button>

          <nav className="site-header__nav site-header__nav--left" aria-label="Main navigation">
            <Link to="/shop?department=Woman" onClick={toTop}>Woman</Link>
            <Link to="/shop?department=Man" onClick={toTop}>Man</Link>
            <Link to="/shop?category=Denim%20Pants" onClick={toTop}>Denim</Link>
            <div className="site-header__more">
              <button type="button" aria-expanded={moreOpen} onClick={() => setMoreOpen((open) => !open)}>
                More
              </button>
              {moreOpen && (
                <div className="site-header__more-menu">
                  <Link to="/about" onClick={toTop}>About</Link>
                  <Link to="/faqs" onClick={toTop}>FAQs</Link>
                  <Link to="/shipping" onClick={toTop}>Shipping</Link>
                  <Link to="/returns" onClick={toTop}>Returns</Link>
                  <Link to="/size-guide" onClick={toTop}>Size guide</Link>
                  <Link to="/care" onClick={toTop}>Materials & care</Link>
                  <Link to="/responsibility" onClick={toTop}>Responsibility</Link>
                  <Link to="/privacy" onClick={toTop}>Privacy</Link>
                  <Link to="/cookies" onClick={toTop}>Cookies</Link>
                  <Link to="/terms" onClick={toTop}>Terms</Link>
                </div>
              )}
            </div>
          </nav>

          <Link className="site-header__logo" to="/" onClick={toTop} aria-label={`${siteConfig.brandName} home`}>
            <img src="/brand-lockup.svg" alt="" />
          </Link>

          <nav className="site-header__nav site-header__nav--right" aria-label="Shop tools">
            <button type="button" onClick={() => setSearchOpen(true)}>
              Search
            </button>
            <Link to="/shop?wishlist=1" onClick={toTop}>Wishlist ({wishlist.length})</Link>
            <Link to="/cartpage" onClick={toTop}>Bag ({cartCount})</Link>
          </nav>
        </div>
      </header>

      <div className={`mobile-menu ${menuOpen ? "mobile-menu--open" : ""}`} aria-hidden={!menuOpen}>
        <button className="panel-close" type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu">
          Close
        </button>
        <nav aria-label="Mobile navigation">
          <Link to="/shop?department=Woman" onClick={toTop}>Woman</Link>
          <Link to="/shop?department=Man" onClick={toTop}>Man</Link>
          <Link to="/shop?category=Denim%20Pants" onClick={toTop}>Denim pants / skirts</Link>
          <Link to="/shop?category=Tops%20%2F%20Jackets" onClick={toTop}>Tops / jackets</Link>
          <Link to="/shop?category=Leather" onClick={toTop}>Leather</Link>
          <Link to="/about" onClick={toTop}>About</Link>
          <Link to="/size-guide" onClick={toTop}>Size guide</Link>
          <Link to="/care" onClick={toTop}>Materials & care</Link>
          <Link to="/responsibility" onClick={toTop}>Responsibility</Link>
          <Link to="/faqs" onClick={toTop}>FAQs</Link>
          <Link to="/privacy" onClick={toTop}>Privacy</Link>
          <Link to="/cookies" onClick={toTop}>Cookies</Link>
        </nav>
      </div>

      <div className={`search-panel ${searchOpen ? "search-panel--open" : ""}`} aria-hidden={!searchOpen}>
        <div className="search-panel__head">
          <p>Search</p>
          <button className="panel-close" type="button" onClick={() => setSearchOpen(false)} aria-label="Close search">
            Close
          </button>
        </div>
        <form className="search-panel__form" onSubmit={submitSearch}>
          <input
            autoFocus={searchOpen}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            type="search"
            placeholder="Search the collection"
            aria-label="Search products"
          />
          <button type="submit">View results</button>
        </form>
        <div className="search-panel__results">
          {results.map((product) => (
            <Link key={product.id} to={`/shopDetails/${product.id}`} className="search-result">
              <img src={product.image} alt="" />
              <span>
                <strong>{product.name}</strong>
                <small>{formatPrice(product.price)}</small>
              </span>
            </Link>
          ))}
          {results.length === 0 && <p className="search-panel__empty">No products found.</p>}
        </div>
      </div>
      {(menuOpen || searchOpen) && (
        <button
          className="site-overlay"
          type="button"
          onClick={() => {
            setMenuOpen(false);
            setSearchOpen(false);
          }}
          aria-label="Close panel"
        />
      )}
    </>
  );
}

export default Header;
